import React from 'react'
import { X, Camera, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { BGVSelectedImage } from './BGVGallerySelector'

interface BGVSelectedImagesProps {
  images: BGVSelectedImage[]
  onRemoveImage: (imageId: string) => void
  onReorderImages?: (images: BGVSelectedImage[]) => void
  maxImages?: number
  className?: string
}

export function BGVSelectedImages({
  images,
  onRemoveImage,
  onReorderImages,
  maxImages = 10,
  className = ''
}: BGVSelectedImagesProps) {
  if (images.length === 0) {
    return null
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString())
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (!onReorderImages) return

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    if (dragIndex === dropIndex) return

    const newImages = [...images]
    const draggedImage = newImages[dragIndex]
    newImages.splice(dragIndex, 1)
    newImages.splice(dropIndex, 0, draggedImage)
    
    onReorderImages(newImages)
  }

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              BGV Gallery Images ({images.length}/{maxImages})
            </span>
          </div>
          <Badge variant="outline" className="text-xs">
            From BGV
          </Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="relative group"
              draggable={!!onReorderImages}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent group-hover:border-primary/50 transition-colors">
                <img
                  src={image.url}
                  alt={image.alt_text || image.caption || `Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Position indicator */}
                <Badge 
                  variant="default" 
                  className="absolute top-2 left-2 text-xs h-5 px-1.5"
                >
                  {index + 1}
                </Badge>

                {/* Remove button */}
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onRemoveImage(image.id)}
                >
                  <X className="h-3 w-3" />
                </Button>

                {/* Image info overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-xs space-y-1">
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] h-4 px-1 bg-white/20 border-white/30 text-white">
                        {image.gallery_title}
                      </Badge>
                    </div>
                    {image.caption && (
                      <p className="line-clamp-2 text-[10px] leading-tight">
                        {image.caption}
                      </p>
                    )}
                  </div>
                </div>

                {/* Drag indicator */}
                {onReorderImages && (
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-move">
                    <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-[1px]" />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Gallery link */}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground truncate">
                  Gallery #{image.gallery_id}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => {
                    // Open BGV gallery in new tab - you might want to configure this URL
                    window.open(`/gallery/${image.gallery_id}`, '_blank')
                  }}
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary info */}
        <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>
              {images.length} image{images.length !== 1 ? 's' : ''} selected
            </span>
            <span>
              {new Set(images.map(img => img.gallery_id)).size} gallar{new Set(images.map(img => img.gallery_id)).size !== 1 ? 'ies' : 'y'}
            </span>
          </div>
          {onReorderImages && (
            <span className="text-muted-foreground">
              Drag to reorder
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}